import type { Office } from "./types"

export const BRIGHTON_SPECIALISMS = [
  "Residential Sales",
  "Lettings",
  "Property Management",
  "New Homes",
  "Commercial",
  "Land & Development",
  "Auctions",
  "Relocation",
] as const

export const BRIGHTON_AREAS = [
  "Brighton",
  "Hove",
  "Kemptown",
  "Rottingdean",
  "Shoreham",
  "Lewes Road",
  "Preston Park",
  "Hanover",
  "Seven Dials",
  "Saltdean",
  "Portslade",
  "Woodingdean",
  "Patcham",
  "Fiveways",
] as const

export const BRIGHTON_OFFICES: Office[] = [
  {
    slug: "knight-and-knoxley-brighton",
    name: "Knight & Knoxley",
    branch: "Brighton",
    address: "Clyde Corner, 2 Clyde Road, Preston Circus",
    postcode: "BN1 4NP",
    phone: "01273 286666",
    website: "https://www.knightandknoxley.com",
    email: "info@knightandknoxley.com",
    description:
      "Knight & Knoxley is a well-established independent estate and lettings agent covering Brighton & Hove, Hastings, and London. Known for their proactive approach and strong local knowledge, they handle residential sales and lettings with a personal touch that has earned them consistently high client reviews.",
    established: 2008,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Brighton", "Hove", "Preston Park", "Fiveways", "Hanover", "Seven Dials"],
    propertyCount: 95,
    avgPrice: "£425,000",
    rating: 4.8,
    reviewCount: 219,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Knight & Knoxley has built a strong reputation in Brighton since 2008, earning consistently high reviews through their proactive, client-first approach. Based at Clyde Corner on Preston Circus, the team brings genuine local knowledge to every instruction, covering Brighton, Hove, Preston Park, Fiveways, Hanover, and Seven Dials.\n\nWith 95 properties typically on their books and an average listing price of £425,000, they operate across a broad cross-section of the Brighton market. Their strength lies in combining personal service with thorough market insight, ensuring sellers and landlords receive informed, realistic advice.\n\nWhether you are buying your first flat in Hanover or selling a family home near Preston Park, Knight & Knoxley deliver the kind of attentive, hands-on service that larger firms often struggle to match.",
    whyChoose: [
      "Rated 4.8/5 from 219 client reviews",
      "Independent agency with genuine local expertise since 2008",
      "Proactive approach covering sales, lettings, and property management",
      "Strong presence across Brighton's most popular neighbourhoods",
    ],
    faq: [
      {
        question: "What areas does Knight & Knoxley cover?",
        answer:
          "Knight & Knoxley covers Brighton, Hove, Preston Park, Fiveways, Hanover, and Seven Dials, with additional reach into Hastings and London through their wider network.",
      },
      {
        question: "What services does Knight & Knoxley offer?",
        answer:
          "Knight & Knoxley provides residential sales, lettings, and property management services. Their team is known for a personal, hands-on approach backed by strong local market knowledge.",
      },
      {
        question: "What are Knight & Knoxley's opening hours?",
        answer:
          "Knight & Knoxley is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "mansell-mctaggart-brighton",
    name: "Mansell McTaggart",
    branch: "Brighton",
    address: "138 Old London Road, Patcham",
    postcode: "BN1 8YA",
    phone: "01273 508955",
    website: "https://www.mansellmctaggart.co.uk/branch/brighton/",
    email: "brighton@mansellmctaggart.co.uk",
    description:
      "Mansell McTaggart is one of Sussex's most respected estate agencies, with over 150 years of heritage across the region. Their Brighton branch in Patcham offers expert local knowledge covering the north Brighton area, with a full range of sales, lettings, and property management services.",
    established: 1870,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Brighton", "Patcham", "Preston Park", "Fiveways", "Woodingdean"],
    propertyCount: 80,
    avgPrice: "£395,000",
    rating: 4.5,
    reviewCount: 187,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Mansell McTaggart is one of Sussex's longest-established estate agencies, with roots dating back to 1870. Their Brighton branch in Patcham on Old London Road draws on over 150 years of heritage, offering expert guidance across north Brighton and the surrounding neighbourhoods.\n\nThe team handles residential sales, lettings, property management, and new homes, with 80 properties typically on their books at an average price of £395,000. This positions them well for families and professionals seeking good value in Brighton's northern suburbs, from Patcham and Preston Park through to Fiveways and Woodingdean.\n\nWith a 4.5-star rating from 187 reviews, Mansell McTaggart Brighton combines the reassurance of a well-known Sussex brand with the local knowledge that only comes from decades of serving the same communities.",
    whyChoose: [
      "Over 150 years of estate agency heritage across Sussex",
      "Rated 4.5/5 from 187 client reviews",
      "Full service: sales, lettings, property management, and new homes",
      "Deep knowledge of north Brighton, Patcham, and surrounding areas",
    ],
    faq: [
      {
        question: "What areas does Mansell McTaggart Brighton cover?",
        answer:
          "Mansell McTaggart Brighton covers Brighton, Patcham, Preston Park, Fiveways, and Woodingdean, with a particular focus on the north Brighton area and surrounding suburbs.",
      },
      {
        question: "What services does Mansell McTaggart offer?",
        answer:
          "Mansell McTaggart Brighton provides residential sales, lettings, property management, and new homes services, drawing on over 150 years of experience across Sussex.",
      },
      {
        question: "What are Mansell McTaggart Brighton's opening hours?",
        answer:
          "Mansell McTaggart Brighton is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "john-hilton-brighton",
    name: "John Hilton Estate Agents",
    branch: "Brighton",
    address: "132–135 Lewes Road",
    postcode: "BN2 3LG",
    phone: "01273 608151",
    website: "https://www.johnhiltons.co.uk",
    email: "sales@johnhiltons.co.uk",
    description:
      "John Hilton Estate Agents is a long-standing independent agency on Lewes Road, well known for serving the Hanover, Elm Grove, and eastern Brighton areas. With decades of local experience, they provide honest valuations and a straightforward, no-nonsense approach to sales and lettings.",
    established: 1990,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Lewes Road", "Hanover", "Brighton", "Kemptown", "Woodingdean"],
    propertyCount: 70,
    avgPrice: "£340,000",
    rating: 4.3,
    reviewCount: 156,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "John Hilton Estate Agents has been a fixture on Lewes Road since 1990, serving the eastern Brighton community with honest valuations and a straightforward, no-nonsense approach. The team's deep familiarity with Hanover, Elm Grove, Lewes Road, and Kemptown means sellers and landlords benefit from genuinely informed pricing and marketing advice.\n\nWith 70 properties typically listed at an average price of £340,000, John Hilton occupies an accessible segment of the Brighton market. This makes them a natural choice for first-time buyers, investors, and families looking for value in some of Brighton's most characterful neighbourhoods.\n\nTheir 4.3-star rating from 156 reviews reflects a consistent track record of delivering results through local expertise and personal service, rather than flashy marketing.",
    whyChoose: [
      "Rated 4.3/5 from 156 client reviews",
      "Over 30 years of experience in eastern Brighton",
      "Specialist knowledge of Hanover, Lewes Road, and Kemptown",
      "Straightforward, honest approach to valuations and sales",
    ],
    faq: [
      {
        question: "What areas does John Hilton Estate Agents cover?",
        answer:
          "John Hilton Estate Agents covers Lewes Road, Hanover, Brighton, Kemptown, and Woodingdean, with particular expertise in the eastern Brighton neighbourhoods around Elm Grove and Bear Road.",
      },
      {
        question: "What services does John Hilton Estate Agents offer?",
        answer:
          "John Hilton provides residential sales, lettings, and property management services. They are known for their honest valuations and straightforward approach to the property market.",
      },
      {
        question: "What are John Hilton Estate Agents' opening hours?",
        answer:
          "John Hilton Estate Agents is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "number-twenty-four-brighton",
    name: "Number Twenty Four",
    branch: "Brighton",
    address: "9 St Georges Place",
    postcode: "BN1 4GB",
    phone: "01273 670253",
    website: "https://www.numbertwentyfour.co.uk",
    email: "hello@numbertwentyfour.co.uk",
    description:
      "Number Twenty Four is a modern, female-founded estate agency bringing a fresh approach to Brighton's property market. With extended opening hours seven days a week and a strong digital-first marketing strategy, they specialise in getting properties noticed and sold quickly across the city.",
    established: 2022,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Brighton", "Hove", "Kemptown", "Seven Dials", "Preston Park", "Hanover"],
    propertyCount: 45,
    avgPrice: "£450,000",
    rating: 4.9,
    reviewCount: 68,
    hours: [
      { days: "Mon–Fri", time: "09:00–20:00" },
      { days: "Sat–Sun", time: "09:00–20:00" },
    ],
    aboutText:
      "Number Twenty Four is a modern, female-founded estate agency that has quickly made its mark on Brighton's property scene since launching in 2022. Based at St Georges Place, they bring a fresh, digital-first approach to selling and letting homes, with extended seven-day opening hours that set them apart from the competition.\n\nWith 45 properties on their books at an average price of £450,000, the team focuses on getting homes noticed through strong online marketing and social media. Their approach resonates with a new generation of sellers and buyers who expect responsiveness and transparency throughout the process.\n\nA 4.9-star rating from 68 reviews speaks for itself. Number Twenty Four may be one of Brighton's newer agencies, but their results and client satisfaction suggest they are here to stay.",
    whyChoose: [
      "Rated 4.9/5 from 68 client reviews",
      "Open seven days a week with extended hours until 20:00",
      "Digital-first marketing strategy designed to maximise exposure",
      "Fresh, modern approach from a female-founded agency",
    ],
    faq: [
      {
        question: "What areas does Number Twenty Four cover?",
        answer:
          "Number Twenty Four covers Brighton, Hove, Kemptown, Seven Dials, Preston Park, and Hanover, with a city-wide approach to residential sales and lettings.",
      },
      {
        question: "What services does Number Twenty Four offer?",
        answer:
          "Number Twenty Four provides residential sales and lettings services, with a strong emphasis on digital marketing, professional photography, and social media promotion to help properties stand out.",
      },
      {
        question: "What are Number Twenty Four's opening hours?",
        answer:
          "Number Twenty Four is open seven days a week from 09:00 to 20:00, offering extended hours that are unusual among Brighton estate agents.",
      },
    ],
  },
  {
    slug: "phillips-and-still-brighton",
    name: "Phillips & Still",
    branch: "Brighton",
    address: "112 Western Road",
    postcode: "BN1 2AB",
    phone: "01273 083312",
    website: "https://www.phillipsandstill.co.uk",
    email: "info@phillipsandstill.co.uk",
    description:
      "Phillips & Still is a respected independent estate agent and letting agency on Western Road, offering a boutique service across Brighton & Hove. Their team combines traditional values with modern marketing techniques, covering everything from Regency conversions to modern apartments.",
    established: 2005,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Brighton", "Hove", "Seven Dials", "Preston Park", "Fiveways"],
    propertyCount: 65,
    avgPrice: "£410,000",
    rating: 4.6,
    reviewCount: 134,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Phillips & Still has been a respected name on Western Road since 2005, providing a boutique, independent estate agency service across Brighton and Hove. Their team combines traditional values with modern marketing techniques, handling everything from Regency conversions to contemporary apartments.\n\nWith 65 properties typically on their books at an average listing price of £410,000, Phillips & Still occupy a solid mid-market position. Their coverage spans Brighton, Hove, Seven Dials, Preston Park, and Fiveways, giving them a broad perspective on the city's diverse property landscape.\n\nA 4.6-star rating from 134 reviews reflects their commitment to personal service and transparent communication. For sellers and landlords who value a knowledgeable, approachable team over corporate formality, Phillips & Still are a strong choice.",
    whyChoose: [
      "Rated 4.6/5 from 134 client reviews",
      "Nearly two decades of experience on Brighton's Western Road",
      "Boutique service combining traditional values with modern marketing",
      "Specialists in Regency conversions and period properties",
    ],
    faq: [
      {
        question: "What areas does Phillips & Still cover?",
        answer:
          "Phillips & Still covers Brighton, Hove, Seven Dials, Preston Park, and Fiveways, with strong knowledge of properties across the central Brighton and Hove area.",
      },
      {
        question: "What services does Phillips & Still offer?",
        answer:
          "Phillips & Still provides residential sales, lettings, and property management services. They specialise in a range of property types, from period Regency conversions to modern apartments.",
      },
      {
        question: "What are Phillips & Still's opening hours?",
        answer:
          "Phillips & Still is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "fine-and-country-brighton",
    name: "Fine & Country",
    branch: "Brighton & Hove",
    address: "46 Church Road, Hove",
    postcode: "BN3 2FN",
    phone: "01273 739911",
    website: "https://www.fineandcountry.co.uk/brighton-and-hove-estate-agents",
    email: "brightonandhove@fineandcountry.com",
    description:
      "Fine & Country Brighton & Hove specialises in premium and luxury property across the city and surrounding countryside. As part of the national Fine & Country network, they offer bespoke marketing with professional photography, video tours, and global reach, ideal for higher-value homes.",
    established: 2003,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Relocation"],
    areas: ["Brighton", "Hove", "Kemptown", "Rottingdean", "Preston Park", "Seven Dials"],
    propertyCount: 50,
    avgPrice: "£785,000",
    rating: 4.7,
    reviewCount: 98,
    hours: [
      { days: "Mon–Fri", time: "08:45–18:00" },
      { days: "Sat", time: "09:00–17:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Fine & Country Brighton & Hove specialises in the premium and luxury end of the property market, with an office on Church Road in Hove. As part of the national Fine & Country network, they bring bespoke marketing capabilities to higher-value homes, including professional photography, video tours, and access to a global audience.\n\nWith an average listing price of £785,000 and 50 properties typically on their books, this is an agency pitched at sellers and buyers operating above the mainstream market. Their coverage includes Brighton, Hove, Kemptown, Rottingdean, Preston Park, and Seven Dials.\n\nA 4.7-star rating from 98 reviews reflects the quality of their service. For those with distinctive or high-value properties, Fine & Country offers the kind of tailored, premium marketing that can make a real difference to the outcome of a sale.",
    whyChoose: [
      "Rated 4.7/5 from 98 client reviews",
      "Average listing price of £785,000, reflecting premium market focus",
      "Bespoke marketing with professional photography and video tours",
      "National and international reach through the Fine & Country network",
    ],
    faq: [
      {
        question: "What areas does Fine & Country Brighton & Hove cover?",
        answer:
          "Fine & Country Brighton & Hove covers Brighton, Hove, Kemptown, Rottingdean, Preston Park, and Seven Dials, with a focus on premium and luxury properties across the city and surrounding countryside.",
      },
      {
        question: "What services does Fine & Country offer?",
        answer:
          "Fine & Country Brighton & Hove provides residential sales, lettings, new homes, and relocation services. They specialise in bespoke marketing for higher-value properties, including professional photography and video tours.",
      },
      {
        question: "What are Fine & Country Brighton & Hove's opening hours?",
        answer:
          "Fine & Country Brighton & Hove is open Monday to Friday from 08:45 to 18:00 and Saturday from 09:00 to 17:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "your-move-brighton",
    name: "Your Move",
    branch: "Brighton & Hove",
    address: "38 Queens Road",
    postcode: "BN1 3XB",
    phone: "01273 283980",
    website: "https://www.your-move.co.uk/estate-agent/brighton",
    email: "brighton@your-move.co.uk",
    description:
      "Your Move Brighton & Hove, located moments from Brighton station on Queens Road, is part of one of the UK's largest estate agency networks. They offer a full range of services including sales, lettings, mortgages, and conveyancing, with the backing of a national brand and strong local presence.",
    established: 1982,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Brighton", "Hove", "Kemptown", "Preston Park", "Hanover", "Lewes Road"],
    propertyCount: 120,
    avgPrice: "£365,000",
    rating: 4.1,
    reviewCount: 245,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Your Move Brighton & Hove, located on Queens Road just moments from Brighton station, is part of one of the UK's largest estate agency networks. Established in 1982, they offer a full range of property services including sales, lettings, mortgages, and conveyancing, all under one roof.\n\nWith 120 properties on their books and an average listing price of £365,000, Your Move handles a high volume of transactions across Brighton, Hove, Kemptown, Preston Park, Hanover, and Lewes Road. Their national network provides access to a large buyer pool, while the local team brings knowledge of Brighton's diverse neighbourhoods.\n\nA 4.1-star rating from 245 reviews demonstrates their reach and activity in the market. For sellers and buyers looking for a well-resourced, full-service agency with strong infrastructure, Your Move is a reliable option.",
    whyChoose: [
      "Rated 4.1/5 from 245 client reviews",
      "Part of one of the UK's largest estate agency networks",
      "Full service including sales, lettings, mortgages, and conveyancing",
      "High-volume agency with 120 properties typically on their books",
    ],
    faq: [
      {
        question: "What areas does Your Move Brighton & Hove cover?",
        answer:
          "Your Move Brighton & Hove covers Brighton, Hove, Kemptown, Preston Park, Hanover, and Lewes Road, with access to a nationwide buyer network through their parent group.",
      },
      {
        question: "What services does Your Move offer?",
        answer:
          "Your Move Brighton & Hove provides residential sales, lettings, property management, new homes, mortgage advice, and conveyancing services. Their full-service approach means everything can be handled in one place.",
      },
      {
        question: "What are Your Move Brighton & Hove's opening hours?",
        answer:
          "Your Move Brighton & Hove is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "q-estate-agents-brighton",
    name: "Q Estate Agents",
    branch: "Brighton",
    address: "195 Lewes Road",
    postcode: "BN2 3LA",
    phone: "01273 767054",
    website: "https://qestateagents.co.uk",
    email: "info@qestateagents.co.uk",
    description:
      "Q Estate Agents is a well-regarded independent agency on Lewes Road, offering lettings and sales services across Brighton & Hove. Known for their hands-on approach and genuine local expertise, they manage a strong portfolio of rental properties alongside residential sales.",
    established: 2004,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Lewes Road", "Hanover", "Brighton", "Kemptown", "Fiveways", "Woodingdean"],
    propertyCount: 85,
    avgPrice: "£350,000",
    rating: 4.4,
    reviewCount: 167,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–14:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Q Estate Agents has been a trusted name on Lewes Road since 2004, offering lettings and sales services across Brighton and Hove. Known for their hands-on approach and genuine local expertise, they manage a strong portfolio of rental properties alongside residential sales.\n\nWith 85 properties on their books at an average price of £350,000, Q Estate Agents are well positioned for the eastern Brighton market. Their coverage spans Lewes Road, Hanover, Kemptown, Fiveways, and Woodingdean, giving them broad reach across some of the city's most popular and affordable neighbourhoods.\n\nA 4.4-star rating from 167 reviews reflects two decades of consistent service. For landlords seeking a reliable management partner or sellers wanting an agent who truly knows the local streets, Q Estate Agents are a solid and experienced choice.",
    whyChoose: [
      "Rated 4.4/5 from 167 client reviews",
      "Over 20 years of experience on Lewes Road since 2004",
      "Strong lettings portfolio alongside residential sales",
      "Deep knowledge of Hanover, Kemptown, and eastern Brighton",
    ],
    faq: [
      {
        question: "What areas does Q Estate Agents cover?",
        answer:
          "Q Estate Agents covers Lewes Road, Hanover, Brighton, Kemptown, Fiveways, and Woodingdean, with particular strength in the eastern Brighton area.",
      },
      {
        question: "What services does Q Estate Agents offer?",
        answer:
          "Q Estate Agents provides residential sales, lettings, and property management services. They are especially well regarded for their lettings and property management expertise.",
      },
      {
        question: "What are Q Estate Agents' opening hours?",
        answer:
          "Q Estate Agents is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 14:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "myhaus-brighton",
    name: "MyHaus",
    branch: "Brighton",
    address: "64 Queens Road",
    postcode: "BN1 3XD",
    phone: "01273 286788",
    website: "https://myhausbrighton.co.uk",
    email: "info@myhausbrighton.co.uk",
    description:
      "MyHaus Brighton is a contemporary estate agent on Queens Road offering a modern, design-led approach to property sales and lettings. Their focus on high-quality photography, video content, and social media marketing helps properties stand out in Brighton's competitive market.",
    established: 2020,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Brighton", "Hove", "Seven Dials", "Preston Park", "Kemptown"],
    propertyCount: 40,
    avgPrice: "£475,000",
    rating: 4.8,
    reviewCount: 40,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "MyHaus Brighton is a contemporary estate agent on Queens Road, bringing a design-led, visually driven approach to the city's property market. Founded in 2020, they have quickly established themselves through high-quality photography, video content, and strategic social media marketing that helps properties stand out.\n\nWith 40 properties typically listed at an average price of £475,000, MyHaus targets the mid-to-upper segment of the Brighton market. Their coverage includes Brighton, Hove, Seven Dials, Preston Park, and Kemptown, appealing to buyers and sellers who appreciate modern presentation and creative marketing.\n\nA 4.8-star rating from 40 reviews shows early promise and strong client satisfaction. For sellers who want their property presented at its absolute best, MyHaus offers a fresh alternative to the more traditional agencies in the city.",
    whyChoose: [
      "Rated 4.8/5 from 40 client reviews",
      "Design-led marketing with professional photography and video",
      "Strong social media presence to maximise property exposure",
      "Modern, contemporary approach to Brighton's property market",
    ],
    faq: [
      {
        question: "What areas does MyHaus cover?",
        answer:
          "MyHaus covers Brighton, Hove, Seven Dials, Preston Park, and Kemptown, with a focus on properties that benefit from high-quality visual marketing.",
      },
      {
        question: "What services does MyHaus offer?",
        answer:
          "MyHaus provides residential sales and lettings, with a distinctive emphasis on design-led marketing, professional photography, video content, and social media promotion.",
      },
      {
        question: "What are MyHaus's opening hours?",
        answer:
          "MyHaus is open Monday to Friday from 09:00 to 17:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "maslen-lewes-road",
    name: "Maslen Estate Agents",
    branch: "Lewes Road",
    address: "39 Lewes Road",
    postcode: "BN2 3HQ",
    phone: "01273 283924",
    website: "https://www.maslen.co.uk/estate-agents-lewes-road",
    email: "lewesroad@maslen.co.uk",
    description:
      "Maslen Estate Agents has been a trusted name on Lewes Road for years, covering Hanover, Elm Grove, Bear Road, and the wider eastern Brighton area. They offer residential sales and lettings with a strong emphasis on local expertise and attentive customer service.",
    established: 2001,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Lewes Road", "Hanover", "Brighton", "Kemptown", "Woodingdean", "Fiveways"],
    propertyCount: 90,
    avgPrice: "£355,000",
    rating: 4.5,
    reviewCount: 201,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Maslen Estate Agents has been a trusted presence on Lewes Road for years, covering Hanover, Elm Grove, Bear Road, and the wider eastern Brighton area. Established in 2001, the team brings a strong emphasis on local expertise and attentive customer service to everything they do.\n\nWith 90 properties on their books at an average price of £355,000, Maslen serves a busy and diverse segment of the Brighton market. Their knowledge of the terraced streets of Hanover, the student-adjacent areas around Lewes Road, and the quieter pockets of Woodingdean gives them a well-rounded perspective.\n\nA 4.5-star rating from 201 reviews reflects years of reliable service. For sellers and landlords who want an agent with genuine street-level knowledge of eastern Brighton, Maslen is a dependable and well-established choice.",
    whyChoose: [
      "Rated 4.5/5 from 201 client reviews",
      "Over two decades of experience in eastern Brighton since 2001",
      "90 properties typically on their books across a broad area",
      "Specialists in Hanover, Lewes Road, and surrounding neighbourhoods",
    ],
    faq: [
      {
        question: "What areas does Maslen Estate Agents cover?",
        answer:
          "Maslen Estate Agents covers Lewes Road, Hanover, Brighton, Kemptown, Woodingdean, and Fiveways, with particular expertise in the eastern Brighton area including Elm Grove and Bear Road.",
      },
      {
        question: "What services does Maslen Estate Agents offer?",
        answer:
          "Maslen provides residential sales, lettings, and property management services. Their experienced team offers attentive, locally informed guidance throughout the buying, selling, or letting process.",
      },
      {
        question: "What are Maslen Estate Agents' opening hours?",
        answer:
          "Maslen Estate Agents is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "sawyer-and-co-hove",
    name: "Sawyer & Co",
    branch: "Hove",
    address: "52 Church Road, Hove",
    postcode: "BN3 2FN",
    phone: "01273 778844",
    website: "https://www.sawyerandco.co.uk",
    email: "hove@sawyerandco.co.uk",
    description:
      "Sawyer & Co is an independent estate and letting agent on Church Road, Hove, with a reputation for straightforward advice and strong results. Their experienced team handles sales and lettings across Hove, Brighton, and the surrounding areas, with a particular focus on period and character properties.",
    established: 2001,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Hove", "Brighton", "Portslade", "Seven Dials", "Preston Park"],
    propertyCount: 75,
    avgPrice: "£430,000",
    rating: 4.6,
    reviewCount: 178,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Sawyer & Co is an independent estate and letting agent based on Church Road in Hove, one of the city's busiest and most desirable high streets. Established in 2001, they have earned a strong reputation for straightforward advice and consistent results across Hove, Brighton, and the surrounding areas.\n\nWith 75 properties on their books at an average price of £430,000, the team handles a healthy mix of period properties, character homes, and modern apartments. Their particular focus on Hove, Portslade, Seven Dials, and Preston Park gives them detailed knowledge of some of the most sought-after postcodes in the area.\n\nA 4.6-star rating from 178 reviews reflects a track record built on honest communication and reliable service. For sellers and landlords who want an experienced, independent agent with genuine Hove expertise, Sawyer & Co are well worth a conversation.",
    whyChoose: [
      "Rated 4.6/5 from 178 client reviews",
      "Independent agency with over 20 years of experience since 2001",
      "Particular strength in period and character properties",
      "Deep knowledge of Hove, Church Road, and surrounding neighbourhoods",
    ],
    faq: [
      {
        question: "What areas does Sawyer & Co cover?",
        answer:
          "Sawyer & Co covers Hove, Brighton, Portslade, Seven Dials, and Preston Park, with particular expertise in the Church Road corridor and surrounding residential streets.",
      },
      {
        question: "What services does Sawyer & Co offer?",
        answer:
          "Sawyer & Co provides residential sales, lettings, and property management services. They are known for their straightforward approach and strong results across Hove and Brighton.",
      },
      {
        question: "What are Sawyer & Co's opening hours?",
        answer:
          "Sawyer & Co is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "mishons-hove",
    name: "Mishons",
    branch: "Hove",
    address: "94 Church Road, Hove",
    postcode: "BN3 2EB",
    phone: "01273 778877",
    website: "https://mishons.com",
    email: "sales@mishons.com",
    description:
      "Mishons is a family-run estate agency on Church Road, Hove, with deep roots in the local property market. Their experienced team offers residential sales and lettings across Brighton & Hove, combining traditional high-street service with effective online marketing to achieve strong results.",
    established: 2010,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Hove", "Brighton", "Portslade", "Seven Dials", "Preston Park"],
    propertyCount: 60,
    avgPrice: "£445,000",
    rating: 4.5,
    reviewCount: 142,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Mishons is a family-run estate agency on Church Road in Hove, bringing a personal touch to the local property market since 2010. Their experienced team offers residential sales and lettings across Brighton and Hove, combining traditional high-street presence with effective online marketing.\n\nWith 60 properties on their books at an average listing price of £445,000, Mishons are well positioned in the Hove mid-market. Their coverage spans Hove, Brighton, Portslade, Seven Dials, and Preston Park, with particular familiarity with the streets and communities either side of Church Road.\n\nA 4.5-star rating from 142 reviews demonstrates steady, reliable service. As a family-run business, they take pride in building relationships with clients rather than treating transactions as numbers. For sellers and landlords seeking a personal, community-focused agency in Hove, Mishons deliver.",
    whyChoose: [
      "Rated 4.5/5 from 142 client reviews",
      "Family-run agency with a personal, community-focused approach",
      "Effective blend of high-street presence and online marketing",
      "Strong knowledge of Hove, Church Road, and surrounding streets",
    ],
    faq: [
      {
        question: "What areas does Mishons cover?",
        answer:
          "Mishons covers Hove, Brighton, Portslade, Seven Dials, and Preston Park, with particular expertise in the Church Road area and surrounding residential neighbourhoods.",
      },
      {
        question: "What services does Mishons offer?",
        answer:
          "Mishons provides residential sales, lettings, and property management services. Their family-run team combines traditional estate agency values with modern marketing techniques.",
      },
      {
        question: "What are Mishons' opening hours?",
        answer:
          "Mishons is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "fox-and-sons-hove",
    name: "Fox & Sons",
    branch: "Hove",
    address: "161 Church Road, Hove",
    postcode: "BN3 2AD",
    phone: "01273 447325",
    website: "https://www.fox-and-sons.co.uk/estate-agents/hove",
    email: "hove@fox-and-sons.co.uk",
    description:
      "Fox & Sons Hove is part of one of the South Coast's most recognised estate agency brands, with a prominent Church Road office. As part of the Connells Group, they combine extensive local knowledge with a national buyer network, covering sales and lettings across Hove and western Brighton.",
    established: 1878,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Hove", "Brighton", "Portslade", "Seven Dials"],
    propertyCount: 110,
    avgPrice: "£420,000",
    rating: 4.2,
    reviewCount: 198,
    hours: [
      { days: "Mon–Fri", time: "08:30–18:00" },
      { days: "Sat", time: "09:00–17:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Fox & Sons Hove occupies a prominent position on Church Road and is part of one of the South Coast's most recognised estate agency brands. With roots stretching back to 1878, they combine extensive local knowledge with the resources of the Connells Group, one of the UK's largest property services companies.\n\nWith 110 properties typically on their books and an average listing price of £420,000, Fox & Sons Hove handles a high volume of transactions across Hove, Brighton, Portslade, and Seven Dials. Their scale means access to a large national buyer database, which can be a genuine advantage for sellers.\n\nA 4.2-star rating from 198 reviews reflects a busy, well-established office. For sellers and buyers who value the reassurance of a long-standing brand with significant market presence, Fox & Sons Hove is a dependable choice with over 140 years of heritage.",
    whyChoose: [
      "Over 140 years of estate agency heritage since 1878",
      "Rated 4.2/5 from 198 client reviews",
      "110 properties typically on their books, demonstrating strong market presence",
      "National buyer network through the Connells Group",
    ],
    faq: [
      {
        question: "What areas does Fox & Sons Hove cover?",
        answer:
          "Fox & Sons Hove covers Hove, Brighton, Portslade, and Seven Dials, with a focus on western Brighton and the Church Road corridor.",
      },
      {
        question: "What services does Fox & Sons offer in Hove?",
        answer:
          "Fox & Sons Hove provides residential sales, lettings, property management, and new homes services. As part of the Connells Group, they also offer access to mortgage advice and conveyancing.",
      },
      {
        question: "What are Fox & Sons Hove's opening hours?",
        answer:
          "Fox & Sons Hove is open Monday to Friday from 08:30 to 18:00 and Saturday from 09:00 to 17:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "hamlyn-smith-hove",
    name: "Hamlyn Smith",
    branch: "Hove",
    address: "50 Goldstone Villas, Hove",
    postcode: "BN3 3RU",
    phone: "01273 762222",
    website: "https://hamlynsmith.co.uk",
    email: "hello@hamlynsmith.co.uk",
    description:
      "Hamlyn Smith is an independent estate and letting agent in Hove offering a personal, boutique service. Their small but experienced team provides honest valuations and tailored marketing across Hove, Brighton, and surrounding areas, with a focus on building lasting client relationships.",
    established: 2019,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Hove", "Brighton", "Portslade", "Seven Dials", "Preston Park"],
    propertyCount: 35,
    avgPrice: "£465,000",
    rating: 4.9,
    reviewCount: 76,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Hamlyn Smith is an independent estate and letting agent in Hove, offering a personal, boutique service from their Goldstone Villas office. Founded in 2019, their small but experienced team provides honest valuations and tailored marketing, prioritising quality over quantity.\n\nWith 35 properties on their books at an average price of £465,000, Hamlyn Smith keeps their portfolio deliberately manageable. This allows them to give each instruction the time and attention it deserves, covering Hove, Brighton, Portslade, Seven Dials, and Preston Park with a hands-on approach.\n\nA 4.9-star rating from 76 reviews is among the highest in the Brighton and Hove area, reflecting the strength of their client relationships. For sellers who want an agent that treats their property as a priority rather than one of hundreds, Hamlyn Smith is an excellent choice.",
    whyChoose: [
      "Rated 4.9/5 from 76 client reviews, among the highest locally",
      "Boutique approach with a deliberately manageable portfolio",
      "Personal service with honest valuations and tailored marketing",
      "Independent agency focused on building lasting client relationships",
    ],
    faq: [
      {
        question: "What areas does Hamlyn Smith cover?",
        answer:
          "Hamlyn Smith covers Hove, Brighton, Portslade, Seven Dials, and Preston Park, with a focus on properties across the Hove and central Brighton area.",
      },
      {
        question: "What services does Hamlyn Smith offer?",
        answer:
          "Hamlyn Smith provides residential sales and lettings services. Their boutique approach means each property receives dedicated attention, from tailored marketing to personalised client communication.",
      },
      {
        question: "What are Hamlyn Smith's opening hours?",
        answer:
          "Hamlyn Smith is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "pearson-keehan-hove",
    name: "Pearson Keehan",
    branch: "Hove",
    address: "95 Portland Road, Hove",
    postcode: "BN3 5DP",
    phone: "01273 206999",
    website: "https://pearsonkeehan.com",
    email: "info@pearsonkeehan.com",
    description:
      "Pearson Keehan is a boutique property agency positioned on Portland Road between the trendy Poets Corner area and the sought-after New Church Road district. They specialise in prime property across Brighton & Hove, offering attentive, expert service for sales and lettings alike.",
    established: 2019,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Hove", "Brighton", "Portslade", "Preston Park"],
    propertyCount: 40,
    avgPrice: "£520,000",
    rating: 4.8,
    reviewCount: 89,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Pearson Keehan is a boutique property agency on Portland Road in Hove, positioned between the trendy Poets Corner area and the sought-after New Church Road district. Founded in 2019, they have quickly established a reputation for attentive, expert service in the prime Hove market.\n\nWith 40 properties at an average listing price of £520,000, Pearson Keehan sits above the mainstream market, catering to sellers and buyers with discerning taste. Their coverage includes Hove, Brighton, Portslade, and Preston Park, with particular familiarity with the streets around Portland Road and the western Hove area.\n\nA 4.8-star rating from 89 reviews confirms strong client satisfaction. For those selling or buying in the upper end of the Hove market, Pearson Keehan combine local insight with a polished, professional service that delivers results.",
    whyChoose: [
      "Rated 4.8/5 from 89 client reviews",
      "Average listing price of £520,000, reflecting prime Hove focus",
      "Boutique agency with expert knowledge of Portland Road and surroundings",
      "Attentive, personalised service for discerning clients",
    ],
    faq: [
      {
        question: "What areas does Pearson Keehan cover?",
        answer:
          "Pearson Keehan covers Hove, Brighton, Portslade, and Preston Park, with particular expertise in the Portland Road area, Poets Corner, and the New Church Road district.",
      },
      {
        question: "What services does Pearson Keehan offer?",
        answer:
          "Pearson Keehan provides residential sales and lettings services. Their boutique approach focuses on attentive, expert service tailored to the prime end of the Brighton and Hove property market.",
      },
      {
        question: "What are Pearson Keehan's opening hours?",
        answer:
          "Pearson Keehan is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "hw-estate-agents-hove",
    name: "HW Estate Agents",
    branch: "Hove",
    address: "80 Goldstone Villas, Hove",
    postcode: "BN3 3RU",
    phone: "01273 359000",
    website: "https://www.hwestateagents.co.uk",
    email: "info@hwestateagents.co.uk",
    description:
      "HW Estate Agents is an independent agency based in Hove with a strong focus on residential sales and lettings across Brighton & Hove and the wider Sussex coast. Their knowledgeable team provides honest advice and proactive service, with a growing reputation in the local market.",
    established: 2012,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Hove", "Brighton", "Portslade", "Shoreham"],
    propertyCount: 55,
    avgPrice: "£440,000",
    rating: 4.5,
    reviewCount: 112,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "HW Estate Agents is an independent agency based on Goldstone Villas in Hove, with a strong focus on residential sales and lettings across Brighton, Hove, and the wider Sussex coast. Founded in 2012, their knowledgeable team provides honest advice and proactive service, with a growing reputation in the local market.\n\nWith 55 properties on their books at an average price of £440,000, HW Estate Agents serve a solid mid-market segment. Their coverage extends across Hove, Brighton, Portslade, and Shoreham, offering a wider geographical reach than many Hove-based competitors.\n\nA 4.5-star rating from 112 reviews reflects consistent, reliable service over more than a decade. For sellers and landlords looking for an independent agent with honest advice and a proactive approach, HW Estate Agents provide a dependable, client-focused alternative to the larger chains.",
    whyChoose: [
      "Rated 4.5/5 from 112 client reviews",
      "Independent agency with over a decade of local experience",
      "Wider geographical coverage including Shoreham and the Sussex coast",
      "Honest, proactive service with a growing local reputation",
    ],
    faq: [
      {
        question: "What areas does HW Estate Agents cover?",
        answer:
          "HW Estate Agents covers Hove, Brighton, Portslade, and Shoreham, with a broad reach along the Sussex coast that extends beyond the central Brighton and Hove area.",
      },
      {
        question: "What services does HW Estate Agents offer?",
        answer:
          "HW Estate Agents provides residential sales, lettings, and property management services. Their team is known for honest advice and a proactive approach to marketing and client communication.",
      },
      {
        question: "What are HW Estate Agents' opening hours?",
        answer:
          "HW Estate Agents is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "mansell-mctaggart-hove",
    name: "Mansell McTaggart",
    branch: "Hove",
    address: "170 Church Road, Hove",
    postcode: "BN3 2DJ",
    phone: "01273 917200",
    website: "https://www.mansellmctaggart.co.uk/branch/hove/",
    email: "hove@mansellmctaggart.co.uk",
    description:
      "Mansell McTaggart's Hove branch sits on Church Road, one of the city's prime high streets. Drawing on over 150 years of estate agency heritage across Sussex, they offer a comprehensive service covering residential sales, lettings, and property management throughout Hove and western Brighton.",
    established: 1870,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Hove", "Brighton", "Portslade", "Seven Dials"],
    propertyCount: 75,
    avgPrice: "£460,000",
    rating: 4.4,
    reviewCount: 165,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Mansell McTaggart's Hove branch sits on Church Road, one of the city's prime high streets, drawing on over 150 years of estate agency heritage across Sussex. Since 1870, the Mansell McTaggart name has been synonymous with reliable, knowledgeable property advice throughout the region.\n\nWith 75 properties typically on their books at an average price of £460,000, the Hove office covers a healthy portion of the western Brighton and Hove market. Their services include residential sales, lettings, property management, and new homes, with a particular understanding of the Church Road corridor and surrounding streets.\n\nA 4.4-star rating from 165 reviews reflects the depth and consistency of their service. For sellers and landlords who want the reassurance of a long-established Sussex brand combined with genuine local knowledge of Hove, Mansell McTaggart is a natural choice.",
    whyChoose: [
      "Over 150 years of estate agency heritage across Sussex",
      "Rated 4.4/5 from 165 client reviews",
      "Church Road office at the heart of Hove's prime high street",
      "Comprehensive service including sales, lettings, management, and new homes",
    ],
    faq: [
      {
        question: "What areas does Mansell McTaggart Hove cover?",
        answer:
          "Mansell McTaggart Hove covers Hove, Brighton, Portslade, and Seven Dials, with particular expertise in the Church Road area and western Brighton neighbourhoods.",
      },
      {
        question: "What services does Mansell McTaggart offer in Hove?",
        answer:
          "Mansell McTaggart Hove provides residential sales, lettings, property management, and new homes services. Their experienced team draws on over 150 years of heritage across the Sussex region.",
      },
      {
        question: "What are Mansell McTaggart Hove's opening hours?",
        answer:
          "Mansell McTaggart Hove is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "foster-and-co-hove",
    name: "Foster & Co",
    branch: "Hove",
    address: "Ground Floor, 10 The Drive, Hove",
    postcode: "BN3 3JA",
    phone: "01273 830919",
    website: "https://fosterandcoestateagents.co.uk",
    email: "hove@fosterandcoestateagents.co.uk",
    description:
      "Foster & Co is an award-winning independent estate agent with offices in Hove and Brighton. Their Hove office on The Drive covers some of the city's most desirable streets, offering residential sales and lettings with a strong emphasis on quality marketing and personal service.",
    established: 2016,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Hove", "Brighton", "Preston Park", "Fiveways", "Seven Dials"],
    propertyCount: 55,
    avgPrice: "£495,000",
    rating: 4.8,
    reviewCount: 145,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Foster & Co is an award-winning independent estate agent with their Hove office located on The Drive, one of the area's most desirable residential streets. Founded in 2016, they have quickly built a strong reputation for quality marketing and personal service across Brighton and Hove.\n\nWith 55 properties on their books at an average price of £495,000, Foster & Co serve the upper-mid market with a focus on presentation and attention to detail. Their coverage spans Hove, Brighton, Preston Park, Fiveways, and Seven Dials, with a particular understanding of the family-friendly streets around The Drive and surrounding areas.\n\nA 4.8-star rating from 145 reviews is a strong endorsement. For sellers who value an agency that invests in marketing quality and takes the time to understand each property's unique appeal, Foster & Co is an outstanding independent option.",
    whyChoose: [
      "Rated 4.8/5 from 145 client reviews",
      "Award-winning independent agency founded in 2016",
      "Strong emphasis on quality marketing and property presentation",
      "Located on The Drive, covering Hove's most desirable streets",
    ],
    faq: [
      {
        question: "What areas does Foster & Co cover?",
        answer:
          "Foster & Co covers Hove, Brighton, Preston Park, Fiveways, and Seven Dials, with particular expertise in the streets around The Drive and the wider central Hove area.",
      },
      {
        question: "What services does Foster & Co offer?",
        answer:
          "Foster & Co provides residential sales, lettings, and property management services. They are known for their quality marketing, including professional photography and well-crafted property descriptions.",
      },
      {
        question: "What are Foster & Co's opening hours?",
        answer:
          "Foster & Co Hove is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "nicholas-james-hove",
    name: "Nicholas James Sales & Lettings",
    branch: "Hove",
    address: "8 The Drive, Hove",
    postcode: "BN3 3JA",
    phone: "01273 917915",
    website: "https://nicholasjamesproperty.co.uk",
    email: "info@nicholasjamesproperty.co.uk",
    description:
      "Nicholas James Sales & Lettings is an independent property agency on The Drive in Hove, offering sales, lettings, auctions, and new homes services. Their local expertise and personal approach have earned them a loyal client base across Brighton & Hove.",
    established: 2017,
    specialisms: ["Residential Sales", "Lettings", "Auctions", "New Homes"],
    areas: ["Hove", "Brighton", "Preston Park", "Seven Dials", "Portslade"],
    propertyCount: 50,
    avgPrice: "£455,000",
    rating: 4.7,
    reviewCount: 98,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Nicholas James Sales & Lettings is an independent property agency on The Drive in Hove, offering a broad range of services including sales, lettings, auctions, and new homes. Founded in 2017, they have built a loyal client base through local expertise and a personal approach.\n\nWith 50 properties on their books at an average price of £455,000, Nicholas James covers a well-rounded segment of the Hove and Brighton market. Their reach extends across Hove, Brighton, Preston Park, Seven Dials, and Portslade, with the added benefit of auction services for those seeking an alternative route to sale.\n\nA 4.7-star rating from 98 reviews reflects strong client satisfaction. For sellers and landlords who want a versatile, independent agent offering more than just the standard sales and lettings service, Nicholas James provides genuine breadth of expertise from their Hove base.",
    whyChoose: [
      "Rated 4.7/5 from 98 client reviews",
      "Broad service range including sales, lettings, auctions, and new homes",
      "Independent agency with a personal, client-focused approach",
      "Located on The Drive in one of Hove's prime residential areas",
    ],
    faq: [
      {
        question: "What areas does Nicholas James cover?",
        answer:
          "Nicholas James covers Hove, Brighton, Preston Park, Seven Dials, and Portslade, with a focus on properties across the central Brighton and Hove area.",
      },
      {
        question: "What services does Nicholas James offer?",
        answer:
          "Nicholas James provides residential sales, lettings, auctions, and new homes services. Their auction service offers an alternative route to sale for those seeking a quicker or more competitive process.",
      },
      {
        question: "What are Nicholas James's opening hours?",
        answer:
          "Nicholas James is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "newlands-property-brighton",
    name: "Newlands Property",
    branch: "Brighton",
    address: "Clarence House, 30–31 North Street",
    postcode: "BN1 1EB",
    phone: "01273 779777",
    website: "https://newlandsproperty.com",
    email: "info@newlandsproperty.com",
    description:
      "Newlands Property is a specialist commercial property agency based in the heart of Brighton with over 15 years of experience in the South East. They advise on office, retail, industrial, and development property across Brighton, Hove, and the wider Sussex coast.",
    established: 2008,
    specialisms: ["Commercial", "Land & Development"],
    areas: ["Brighton", "Hove", "Lewes Road", "Kemptown", "Portslade", "Shoreham"],
    propertyCount: 30,
    avgPrice: "£550,000",
    rating: 4.6,
    reviewCount: 42,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat–Sun", time: "Closed" },
    ],
    aboutText:
      "Newlands Property is a specialist commercial property agency based in the heart of Brighton on North Street, with over 15 years of experience in the South East. Since 2008, they have been advising on office, retail, industrial, and development property across Brighton, Hove, and the wider Sussex coast.\n\nWith 30 properties on their books at an average value of £550,000, Newlands occupies a niche but important position in Brighton's property market. Their commercial and land expertise makes them the go-to agency for business owners, investors, and developers seeking professional advice on non-residential property.\n\nA 4.6-star rating from 42 reviews reflects the quality of their specialist service. For commercial property needs in Brighton and the surrounding area, Newlands Property brings focused expertise that generalist residential agents simply cannot match.",
    whyChoose: [
      "Rated 4.6/5 from 42 client reviews",
      "Over 15 years of specialist commercial property experience",
      "Expert advice on office, retail, industrial, and development property",
      "Brighton city centre location with broad South East coverage",
    ],
    faq: [
      {
        question: "What areas does Newlands Property cover?",
        answer:
          "Newlands Property covers Brighton, Hove, Lewes Road, Kemptown, Portslade, and Shoreham, with a broader reach across the South East for commercial and development property.",
      },
      {
        question: "What services does Newlands Property offer?",
        answer:
          "Newlands Property specialises in commercial property and land, advising on office, retail, industrial, and development opportunities. They provide sales, lettings, and advisory services for non-residential property.",
      },
      {
        question: "What are Newlands Property's opening hours?",
        answer:
          "Newlands Property is open Monday to Friday from 09:00 to 17:30. They are closed on Saturdays and Sundays.",
      },
    ],
  },
  {
    slug: "property-moves-hove",
    name: "Property Moves",
    branch: "Hove",
    address: "109 Church Road, Hove",
    postcode: "BN3 2AF",
    phone: "01273 321333",
    website: "https://www.propertymoves.co.uk",
    email: "enquiries@propertymoves.co.uk",
    description:
      "Property Moves has been one of Brighton & Hove's leading independent letting agents since 1993. Dedicated exclusively to lettings and property management, they bring over 30 years of focused expertise to landlords and tenants across the city and surrounding areas.",
    established: 1993,
    specialisms: ["Lettings", "Property Management"],
    areas: ["Hove", "Brighton", "Preston Park", "Seven Dials", "Portslade"],
    propertyCount: 150,
    avgPrice: "£1,400/month",
    rating: 4.3,
    reviewCount: 134,
    hours: [
      { days: "Mon–Thu", time: "09:00–17:30" },
      { days: "Fri", time: "09:00–17:00" },
      { days: "Sat", time: "09:00–15:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Property Moves has been one of Brighton and Hove's leading independent letting agents since 1993, bringing over 30 years of focused expertise to the lettings and property management market. Based on Church Road in Hove, they are dedicated exclusively to lettings, which gives them a depth of specialist knowledge that generalist agents rarely match.\n\nWith 150 properties under management at an average rent of £1,400 per month, Property Moves handles one of the largest rental portfolios in the area. Their coverage spans Hove, Brighton, Preston Park, Seven Dials, and Portslade, serving both landlords and tenants with experienced, professional guidance.\n\nA 4.3-star rating from 134 reviews reflects decades of reliable service. For landlords seeking a dedicated lettings specialist with a proven track record and substantial local portfolio, Property Moves is a compelling choice.",
    whyChoose: [
      "Rated 4.3/5 from 134 client reviews",
      "Over 30 years as a dedicated lettings and management specialist",
      "150 properties under management, one of the largest local portfolios",
      "Focused exclusively on lettings, bringing specialist depth of knowledge",
    ],
    faq: [
      {
        question: "What areas does Property Moves cover?",
        answer:
          "Property Moves covers Hove, Brighton, Preston Park, Seven Dials, and Portslade, with a focus on lettings and property management across the central Brighton and Hove area.",
      },
      {
        question: "What services does Property Moves offer?",
        answer:
          "Property Moves specialises exclusively in lettings and property management. They do not handle sales, which allows them to focus entirely on providing expert service to landlords and tenants.",
      },
      {
        question: "What are Property Moves' opening hours?",
        answer:
          "Property Moves is open Monday to Thursday from 09:00 to 17:30, Friday from 09:00 to 17:00, and Saturday from 09:00 to 15:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "fox-and-sons-rottingdean",
    name: "Fox & Sons",
    branch: "Rottingdean",
    address: "50 High Street, Rottingdean",
    postcode: "BN2 7HF",
    phone: "01273 309968",
    website: "https://www.fox-and-sons.co.uk/estate-agents/rottingdean",
    email: "rottingdean@fox-and-sons.co.uk",
    description:
      "Fox & Sons Rottingdean occupies a prominent position on the village high street, opposite the White Horse Hotel. They cover sales and lettings across Rottingdean, Saltdean, Woodingdean, and the coastal villages east of Brighton, with strong local knowledge and national network backing.",
    established: 1878,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Rottingdean", "Saltdean", "Woodingdean", "Brighton", "Kemptown"],
    propertyCount: 65,
    avgPrice: "£385,000",
    rating: 4.3,
    reviewCount: 143,
    hours: [
      { days: "Mon–Fri", time: "08:30–18:00" },
      { days: "Sat", time: "09:00–17:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Fox & Sons Rottingdean occupies a prominent position on the village high street, opposite the White Horse Hotel. As part of the Connells Group with roots stretching back to 1878, they bring national infrastructure and a large buyer database to the intimate coastal village market east of Brighton.\n\nWith 65 properties on their books at an average price of £385,000, the team covers sales and lettings across Rottingdean, Saltdean, Woodingdean, and Kemptown. Their strong local knowledge of the coastal villages, combined with the resources of a major national group, gives sellers access to both local expertise and a wider audience.\n\nA 4.3-star rating from 143 reviews reflects consistent performance. For sellers and buyers in Brighton's eastern coastal villages who want the security of an established national brand with genuine village-level knowledge, Fox & Sons Rottingdean is a solid and well-resourced option.",
    whyChoose: [
      "Over 140 years of estate agency heritage since 1878",
      "Rated 4.3/5 from 143 client reviews",
      "Prominent high street position in the heart of Rottingdean village",
      "National buyer network combined with local coastal village expertise",
    ],
    faq: [
      {
        question: "What areas does Fox & Sons Rottingdean cover?",
        answer:
          "Fox & Sons Rottingdean covers Rottingdean, Saltdean, Woodingdean, Brighton, and Kemptown, with a particular focus on the coastal villages east of Brighton.",
      },
      {
        question: "What services does Fox & Sons offer in Rottingdean?",
        answer:
          "Fox & Sons Rottingdean provides residential sales, lettings, and property management services. As part of the Connells Group, they also offer access to mortgage advice and conveyancing.",
      },
      {
        question: "What are Fox & Sons Rottingdean's opening hours?",
        answer:
          "Fox & Sons Rottingdean is open Monday to Friday from 08:30 to 18:00 and Saturday from 09:00 to 17:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "ian-hyder-rottingdean",
    name: "Ian Hyder & Company",
    branch: "Rottingdean",
    address: "27–29 Marine Drive, Rottingdean",
    postcode: "BN2 7HJ",
    phone: "01273 309309",
    website: "https://www.ianhyder.co.uk",
    email: "info@ianhyder.co.uk",
    description:
      "Ian Hyder & Company operates from extensively refurbished offices on Marine Drive in Rottingdean, prominently positioned on the A259 coast road. A well-established local agent, they specialise in residential sales across Rottingdean, Saltdean, Woodingdean, and the surrounding villages.",
    established: 1985,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Rottingdean", "Saltdean", "Woodingdean", "Brighton", "Kemptown"],
    propertyCount: 45,
    avgPrice: "£410,000",
    rating: 4.6,
    reviewCount: 137,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Ian Hyder & Company operates from extensively refurbished offices on Marine Drive in Rottingdean, prominently positioned on the A259 coast road. Established in 1985, they bring nearly 40 years of local experience to the residential property market in Brighton's eastern coastal villages.\n\nWith 45 properties on their books at an average price of £410,000, the team specialises in sales and lettings across Rottingdean, Saltdean, Woodingdean, and into Kemptown. Their seafront location and deep familiarity with the area make them a natural point of contact for buyers drawn to the coastal village lifestyle.\n\nA 4.6-star rating from 137 reviews demonstrates a long track record of quality service. For sellers and buyers in Rottingdean and the surrounding coastal villages, Ian Hyder & Company offer the kind of established, locally rooted expertise that newer agencies take years to develop.",
    whyChoose: [
      "Rated 4.6/5 from 137 client reviews",
      "Nearly 40 years of local experience since 1985",
      "Prominent seafront office on the A259 coast road",
      "Specialist knowledge of Rottingdean, Saltdean, and coastal villages",
    ],
    faq: [
      {
        question: "What areas does Ian Hyder & Company cover?",
        answer:
          "Ian Hyder & Company covers Rottingdean, Saltdean, Woodingdean, Brighton, and Kemptown, with specialist knowledge of the coastal villages east of Brighton.",
      },
      {
        question: "What services does Ian Hyder & Company offer?",
        answer:
          "Ian Hyder & Company provides residential sales and lettings services. Their experienced team specialises in properties across the coastal villages, from family homes to retirement properties and seaside apartments.",
      },
      {
        question: "What are Ian Hyder & Company's opening hours?",
        answer:
          "Ian Hyder & Company is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "david-webb-rottingdean",
    name: "David Webb Residential",
    branch: "Rottingdean",
    address: "12 West Street, Rottingdean",
    postcode: "BN2 7HP",
    phone: "01273 300525",
    website: "https://www.davidwebbresidential.co.uk",
    email: "info@davidwebbresidential.co.uk",
    description:
      "David Webb Residential is a boutique estate agent in the heart of Rottingdean village, offering personalised sales and lettings services. Their intimate knowledge of the coastal villages east of Brighton, combined with an attentive approach, has earned them strong client reviews.",
    established: 2017,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Rottingdean", "Saltdean", "Woodingdean", "Brighton"],
    propertyCount: 30,
    avgPrice: "£435,000",
    rating: 4.8,
    reviewCount: 86,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "David Webb Residential is a boutique estate agent in the heart of Rottingdean village, offering personalised sales and lettings services from their West Street office. Founded in 2017, they have built a strong reputation through attentive service and intimate knowledge of the coastal villages east of Brighton.\n\nWith 30 properties on their books at an average price of £435,000, David Webb keeps their portfolio deliberately focused. This allows the team to give each instruction the care and attention it deserves, covering Rottingdean, Saltdean, Woodingdean, and Brighton with a genuinely personal touch.\n\nA 4.8-star rating from 86 reviews places them among the highest-rated agents in the area. For sellers who value a boutique, relationship-driven approach over volume and scale, David Webb Residential is an outstanding option in the Rottingdean market.",
    whyChoose: [
      "Rated 4.8/5 from 86 client reviews",
      "Boutique agency in the heart of Rottingdean village",
      "Personalised service with a deliberately focused property portfolio",
      "Intimate knowledge of coastal villages east of Brighton",
    ],
    faq: [
      {
        question: "What areas does David Webb Residential cover?",
        answer:
          "David Webb Residential covers Rottingdean, Saltdean, Woodingdean, and Brighton, with a particular focus on the coastal villages and their surrounding residential streets.",
      },
      {
        question: "What services does David Webb Residential offer?",
        answer:
          "David Webb Residential provides residential sales and lettings services. Their boutique approach means each property benefits from personalised marketing and dedicated client communication.",
      },
      {
        question: "What are David Webb Residential's opening hours?",
        answer:
          "David Webb Residential is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "erdmans-saltdean",
    name: "Erdmans",
    branch: "Saltdean",
    address: "7 Longridge Avenue, Saltdean",
    postcode: "BN2 8LG",
    phone: "01273 121891",
    website: "https://www.erdmans.co.uk",
    email: "info@erdmans.co.uk",
    description:
      "Erdmans is a family-run estate agency on Saltdean High Street, offering a personal and hands-on approach to property sales and lettings. Their local knowledge of Saltdean, Rottingdean, and the surrounding coastal areas is second to none, making them a trusted choice for village-centric property needs.",
    established: 2023,
    specialisms: ["Residential Sales", "Lettings"],
    areas: ["Saltdean", "Rottingdean", "Woodingdean", "Brighton"],
    propertyCount: 25,
    avgPrice: "£395,000",
    rating: 4.9,
    reviewCount: 34,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Erdmans is a family-run estate agency on Saltdean High Street, offering a personal and hands-on approach to property sales and lettings. Founded in 2023, they are one of the newest agencies in the area, but their local knowledge of Saltdean, Rottingdean, and the surrounding coastal villages is second to none.\n\nWith 25 properties on their books at an average price of £395,000, Erdmans keeps things focused and personal. Their small team ensures every client receives individual attention, and their village-centric approach means they understand the specific appeal and character of the Saltdean and Rottingdean market.\n\nA 4.9-star rating from 34 reviews is an impressive start. For sellers and buyers who prefer the warmth and accountability of a family-run business over a corporate chain, Erdmans bring fresh energy and genuine commitment to the local property market.",
    whyChoose: [
      "Rated 4.9/5 from 34 client reviews",
      "Family-run agency with a genuinely personal approach",
      "Unrivalled local knowledge of Saltdean and surrounding villages",
      "Focused portfolio ensuring every property receives dedicated attention",
    ],
    faq: [
      {
        question: "What areas does Erdmans cover?",
        answer:
          "Erdmans covers Saltdean, Rottingdean, Woodingdean, and Brighton, with a particular focus on the coastal village communities east of Brighton.",
      },
      {
        question: "What services does Erdmans offer?",
        answer:
          "Erdmans provides residential sales and lettings services. As a family-run agency, they offer a personal, hands-on approach to every instruction, from initial valuation through to completion.",
      },
      {
        question: "What are Erdmans' opening hours?",
        answer:
          "Erdmans is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "brand-vaughan-kemptown",
    name: "Brand Vaughan",
    branch: "Kemptown",
    address: "110 St George's Road, Kemptown",
    postcode: "BN2 1EA",
    phone: "01273 683111",
    website: "https://www.brandvaughan.co.uk/branch-finder/kemptown/contact-us/",
    email: "kemptown@brandvaughan.co.uk",
    description:
      "Brand Vaughan Kemptown is part of one of Brighton & Hove's most established independent estate agencies. Their St George's Road office covers the vibrant Kemptown and east Brighton area, offering residential sales and lettings backed by decades of local market experience.",
    established: 1990,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Kemptown", "Brighton", "Hanover", "Lewes Road"],
    propertyCount: 85,
    avgPrice: "£380,000",
    rating: 4.4,
    reviewCount: 176,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Brand Vaughan Kemptown is part of one of Brighton and Hove's most established independent estate agencies. Their St George's Road office covers the vibrant Kemptown and east Brighton area, drawing on decades of local market experience to deliver strong results for sellers and landlords.\n\nWith 85 properties on their books at an average price of £380,000, Brand Vaughan serves a busy segment of the Brighton market. Their coverage includes Kemptown, Brighton, Hanover, and Lewes Road, with deep understanding of the characterful streets, Regency squares, and converted flats that define east Brighton.\n\nA 4.4-star rating from 176 reviews reflects consistent, reliable performance. As an established independent agency with multiple offices across the city, Brand Vaughan brings both local expertise and cross-city reach that benefits sellers looking to attract buyers from across Brighton and Hove.",
    whyChoose: [
      "Rated 4.4/5 from 176 client reviews",
      "Part of one of Brighton's most established independent agencies",
      "Deep knowledge of Kemptown, Hanover, and east Brighton",
      "Cross-city reach through multiple Brand Vaughan offices",
    ],
    faq: [
      {
        question: "What areas does Brand Vaughan Kemptown cover?",
        answer:
          "Brand Vaughan Kemptown covers Kemptown, Brighton, Hanover, and Lewes Road, with particular expertise in east Brighton's Regency properties, converted flats, and characterful terraced streets.",
      },
      {
        question: "What services does Brand Vaughan offer?",
        answer:
          "Brand Vaughan Kemptown provides residential sales, lettings, and property management services. Their experienced team draws on decades of local market knowledge to guide sellers and landlords effectively.",
      },
      {
        question: "What are Brand Vaughan Kemptown's opening hours?",
        answer:
          "Brand Vaughan Kemptown is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "fox-and-sons-kemptown",
    name: "Fox & Sons",
    branch: "Kemptown",
    address: "9–10 Bristol Road, Kemptown",
    postcode: "BN2 1AP",
    phone: "01273 688148",
    website: "https://www.fox-and-sons.co.uk/estate-agents/kemp-town-brighton",
    email: "kemptown@fox-and-sons.co.uk",
    description:
      "Fox & Sons Kemptown has a prominent office on Bristol Road in the heart of Kemp Town village. As part of the Connells Group, they offer residential sales and lettings with the backing of a national network and extensive local knowledge of east Brighton's diverse property market.",
    established: 1878,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Kemptown", "Brighton", "Hanover", "Lewes Road", "Rottingdean"],
    propertyCount: 90,
    avgPrice: "£370,000",
    rating: 4.2,
    reviewCount: 189,
    hours: [
      { days: "Mon–Fri", time: "08:30–18:00" },
      { days: "Thu", time: "08:30–19:00" },
      { days: "Sat", time: "09:00–17:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Fox & Sons Kemptown has a prominent office on Bristol Road in the heart of Kemp Town village. As part of the Connells Group with heritage dating back to 1878, they offer residential sales and lettings backed by national infrastructure and an extensive buyer database.\n\nWith 90 properties on their books at an average price of £370,000, this is one of the busier agencies in east Brighton. Their coverage spans Kemptown, Brighton, Hanover, Lewes Road, and Rottingdean, offering broad reach across the eastern side of the city. Extended Thursday evening hours until 19:00 provide additional flexibility for working buyers and sellers.\n\nA 4.2-star rating from 189 reviews reflects a high volume of transactions and consistent service. For sellers and buyers in Kemptown and east Brighton who want the backing of a well-established national brand with genuine local presence, Fox & Sons is a reliable and well-resourced option.",
    whyChoose: [
      "Over 140 years of estate agency heritage since 1878",
      "Rated 4.2/5 from 189 client reviews",
      "90 properties on their books, demonstrating strong market activity",
      "Extended Thursday opening hours until 19:00 for added flexibility",
    ],
    faq: [
      {
        question: "What areas does Fox & Sons Kemptown cover?",
        answer:
          "Fox & Sons Kemptown covers Kemptown, Brighton, Hanover, Lewes Road, and Rottingdean, with a strong focus on the diverse property market across east Brighton.",
      },
      {
        question: "What services does Fox & Sons offer in Kemptown?",
        answer:
          "Fox & Sons Kemptown provides residential sales, lettings, property management, and new homes services. As part of the Connells Group, they also offer access to mortgage advice and conveyancing.",
      },
      {
        question: "What are Fox & Sons Kemptown's opening hours?",
        answer:
          "Fox & Sons Kemptown is open Monday to Friday from 08:30 to 18:00, with extended hours on Thursday until 19:00. Saturday hours are 09:00 to 17:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "the-property-shop-brighton",
    name: "The Property Shop Brighton",
    branch: "Kemptown",
    address: "50 St James's Street, Kemptown",
    postcode: "BN2 1QG",
    phone: "01273 915400",
    website: "https://www.tpsb.co.uk",
    email: "info@tpsb.co.uk",
    description:
      "The Property Shop Brighton is an independent estate and letting agent on St James's Street in Kemptown. They offer a comprehensive service across Brighton & Hove, with particular expertise in Kemptown, Queen's Park, and the surrounding areas. Their approachable team prides itself on honest, jargon-free advice.",
    established: 2012,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Kemptown", "Brighton", "Hanover", "Lewes Road"],
    propertyCount: 70,
    avgPrice: "£360,000",
    rating: 4.6,
    reviewCount: 154,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "The Property Shop Brighton is an independent estate and letting agent on St James's Street in Kemptown, offering a comprehensive service with a particular focus on Kemptown, Queen's Park, and the surrounding east Brighton neighbourhoods. Founded in 2012, their approachable team prides itself on honest, jargon-free advice.\n\nWith 70 properties on their books at an average price of £360,000, they serve a diverse mix of buyers and sellers across an accessible price range. Their coverage includes Kemptown, Brighton, Hanover, and Lewes Road, with the kind of street-level knowledge that comes from being embedded in the community.\n\nA 4.6-star rating from 154 reviews reflects a strong commitment to client service. For those buying, selling, or letting in Kemptown and east Brighton who want an independent agent that communicates clearly and puts clients first, The Property Shop Brighton is a reliable and well-regarded choice.",
    whyChoose: [
      "Rated 4.6/5 from 154 client reviews",
      "Independent agency with over a decade of Kemptown experience",
      "Honest, jargon-free approach to property advice",
      "Strong community presence on St James's Street in Kemptown",
    ],
    faq: [
      {
        question: "What areas does The Property Shop Brighton cover?",
        answer:
          "The Property Shop Brighton covers Kemptown, Brighton, Hanover, and Lewes Road, with particular expertise in the St James's Street area, Queen's Park, and surrounding east Brighton neighbourhoods.",
      },
      {
        question: "What services does The Property Shop Brighton offer?",
        answer:
          "The Property Shop Brighton provides residential sales, lettings, and property management services. Their team is known for honest, straightforward advice without unnecessary jargon.",
      },
      {
        question: "What are The Property Shop Brighton's opening hours?",
        answer:
          "The Property Shop Brighton is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "leaders-kemptown",
    name: "Leaders Letting & Estates",
    branch: "Kemptown",
    address: "96–97 St Georges Road, Kemptown",
    postcode: "BN2 1EE",
    phone: "01273 675571",
    website: "https://www.leaders.co.uk/our-offices/east-sussex/estate-agents-in-brighton-kemptown/",
    email: "brightonkemptown@leaders.co.uk",
    description:
      "Leaders Kemptown is part of the national Leaders network, one of the UK's largest lettings groups. Their St Georges Road office covers sales and lettings across Kemptown, east Brighton, and the surrounding areas, offering professional service backed by a nationwide infrastructure.",
    established: 1983,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Kemptown", "Brighton", "Hanover", "Lewes Road", "Rottingdean"],
    propertyCount: 100,
    avgPrice: "£345,000",
    rating: 4.1,
    reviewCount: 167,
    hours: [
      { days: "Mon–Fri", time: "08:30–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Leaders Kemptown is part of the national Leaders network, one of the UK's largest lettings groups. Their St Georges Road office has been serving Kemptown and east Brighton since 1983, offering sales and lettings backed by nationwide infrastructure and professional service standards.\n\nWith 100 properties on their books at an average price of £345,000, Leaders handles a high volume of transactions across Kemptown, Brighton, Hanover, Lewes Road, and Rottingdean. Their scale means access to a large tenant and buyer database, which can be a genuine advantage for landlords and sellers seeking quick results.\n\nA 4.1-star rating from 167 reviews reflects a busy, well-established office. For landlords and sellers in east Brighton who want the resources and reach of a national network combined with a local office team, Leaders Kemptown offers a well-supported, professional service.",
    whyChoose: [
      "Rated 4.1/5 from 167 client reviews",
      "Part of one of the UK's largest lettings networks since 1983",
      "100 properties typically on their books, showing strong market activity",
      "National infrastructure providing a large buyer and tenant database",
    ],
    faq: [
      {
        question: "What areas does Leaders Kemptown cover?",
        answer:
          "Leaders Kemptown covers Kemptown, Brighton, Hanover, Lewes Road, and Rottingdean, with a focus on the eastern Brighton property market including both sales and lettings.",
      },
      {
        question: "What services does Leaders Kemptown offer?",
        answer:
          "Leaders Kemptown provides residential sales, lettings, and property management services. As part of the national Leaders network, they benefit from a large buyer and tenant database and professional support infrastructure.",
      },
      {
        question: "What are Leaders Kemptown's opening hours?",
        answer:
          "Leaders Kemptown is open Monday to Friday from 08:30 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "oakley-property-shoreham",
    name: "Oakley Property",
    branch: "Shoreham",
    address: "380 Brighton Road, Shoreham-by-Sea",
    postcode: "BN43 6RE",
    phone: "01273 283673",
    website: "https://oakleyproperty.com",
    email: "info@oakleyproperty.com",
    description:
      "Oakley Property is an independent estate agent on Brighton Road in Shoreham-by-Sea, covering sales and lettings across Shoreham, Southwick, Lancing, and the wider Adur district. Their local team offers a friendly, personal service with strong knowledge of the coastal property market west of Brighton.",
    established: 2017,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Shoreham", "Portslade", "Hove"],
    propertyCount: 60,
    avgPrice: "£375,000",
    rating: 4.7,
    reviewCount: 108,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Oakley Property is an independent estate agent on Brighton Road in Shoreham-by-Sea, covering sales and lettings across Shoreham, Southwick, Lancing, and the wider Adur district. Founded in 2017, their local team offers a friendly, personal service with strong knowledge of the coastal property market west of Brighton.\n\nWith 60 properties on their books at an average price of £375,000, Oakley Property serves a growing market along the Sussex coast. Their coverage includes Shoreham, Portslade, and Hove, bridging the gap between central Brighton and the communities further west along the coast road.\n\nA 4.7-star rating from 108 reviews reflects the quality of their client relationships. For sellers and landlords in Shoreham and the surrounding Adur area who want an independent, locally focused agent with genuine understanding of the coastal market, Oakley Property is an excellent choice.",
    whyChoose: [
      "Rated 4.7/5 from 108 client reviews",
      "Independent agency with strong knowledge of the Adur coastal market",
      "Friendly, personal service from a locally rooted team",
      "Broad coverage from Shoreham through Portslade to Hove",
    ],
    faq: [
      {
        question: "What areas does Oakley Property cover?",
        answer:
          "Oakley Property covers Shoreham, Portslade, and Hove, with a wider reach across Southwick, Lancing, and the Adur district along the Sussex coast.",
      },
      {
        question: "What services does Oakley Property offer?",
        answer:
          "Oakley Property provides residential sales, lettings, and property management services. Their team specialises in the coastal property market west of Brighton, offering knowledgeable, personal guidance.",
      },
      {
        question: "What are Oakley Property's opening hours?",
        answer:
          "Oakley Property is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
]
